import * as React from "react";
import KuikaButton, { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../kuika-button/kuika-button";
import KuikaPageHeader from "../kuika-page-header/kuika-page-header";
import "./kuika-editor-header.scss";

/**
 * The top bar an editor that is a FORM opens with: what you are editing on the left, the
 * control that commits it on the right.
 *
 * It is `KuikaPageHeader`'s bar with the commit pair filled in, kept as its own component
 * because the editors that need it are not all built the same way. The datasource sheets
 * are forms (`KuikaFormPage` draws this inside its `<Form>`); the table-columns editor and
 * the session-variable editor own their bodies and only want the chrome.
 *
 * There is no CANCEL. It ran the same close path the back control does, so two controls
 * promised two behaviours and delivered one — and every tool that had already grown a back
 * arrow (Scheduled Jobs, the Email Builder, the API Builder, Config) had dropped it. Back
 * carries the id the suites resolved on CANCEL.
 */

export interface KuikaEditorHeaderProps {
  /** Name the target, e.g. "dbo.Orders" — always pass it, even when `renderTitle` draws it. */
  title: string;
  /**
   * A bare glyph (16-20px `KuikaSvg`), not the gradient tile — that one is for dialogs. Only for
   * a bar whose name is a plain heading: a switcher chip or an identity in `renderTitle` carries
   * its own glyph, and a second one here would name the record twice.
   */
  icon?: React.ReactNode;
  /**
   * Replaces the heading node with a caller-owned control — a `KuikaEditorTitle` with the
   * editor's switcher chip and its bound rename field. Inside a form, a field nested in a
   * heading is not announced as its name, hence `title` as well.
   */
  renderTitle?: (title: string) => React.ReactNode;
  /**
   * A control that belongs to the whole editor rather than to one end of the bar — the table
   * editor's Columns | Data tabs. `KuikaPageHeader` centres it on the ROW, so it holds still as
   * the name's length changes; the name is what gives way when the bar runs short.
   */
  center?: React.ReactNode;
  /** Editor-level controls that do not commit anything — a mode switch, a toggle. */
  extra?: React.ReactNode;
  /** Sits left of the commit button: an action that reaches the target without saving. */
  leading?: React.ReactNode;
  submitLabel: string;
  /** Omit when `submitHtmlType` is "submit" — the form the button sits in commits it. */
  onSubmit?: () => void;
  /** "submit" hands the click to the surrounding form, as the antd headers did. */
  submitHtmlType?: "button" | "submit";
  /**
   * Leaving the editor. The back control is the only way out, so this must be the screen's
   * GUARDED close — the one that offers to save first — never a raw cancel.
   */
  onCancel: () => void;
  /** What the back control says it returns to: "Back to Datasources". */
  backLabel?: string;
  /** Dims nothing here; it disables back and spins the commit button. */
  isLoading?: boolean;
  /**
   * The stateful commit (KuikaButton `status`): the commit rolls to "Saving", then "Saved" or "Try
   * again", and its width follows — the editor stays open, so the button is the confirmation. When
   * given, `isLoading` is ignored for the commit.
   */
  status?: KuikaButtonStatus;
  statusLabels?: IKuikaButtonStatusLabels;
  isSubmitDisabled?: boolean;
  /**
   * Wraps the commit button — for a confirmation the caller owns. It receives the button
   * this bar would have rendered, so it keeps its look and its test id either way.
   */
  renderSubmit?: (submitButton: React.ReactNode) => React.ReactNode;
  /** Goes on the back control, which is the close control now. */
  cancelTestId?: string;
  submitTestId?: string;
  /** The product tour targets the pair by these, so a screen that has them keeps them. */
  cancelOnboardingId?: string;
  submitOnboardingId?: string;
  className?: string;
}

export const KuikaEditorHeader = ({
  title,
  icon,
  renderTitle,
  center,
  extra,
  leading,
  submitLabel,
  onSubmit,
  submitHtmlType,
  onCancel,
  backLabel = "Back",
  isLoading,
  status,
  statusLabels,
  isSubmitDisabled,
  renderSubmit,
  cancelTestId,
  submitTestId,
  cancelOnboardingId,
  submitOnboardingId,
  className
}: KuikaEditorHeaderProps) => {
  const submitButton: React.ReactNode = (
    <KuikaButton
      type="primary"
      size="md"
      uppercase
      htmlType={submitHtmlType}
      loading={isLoading}
      status={status}
      statusLabels={statusLabels}
      disabled={isSubmitDisabled}
      onClick={onSubmit}
      data-testid={submitTestId}
      data-onboarding-id={submitOnboardingId}
    >
      {submitLabel}
    </KuikaButton>
  );

  // The caller's name row in a group that may shrink (`min-width: 0`), so a long name clips
  // instead of pushing the centred control off its midline. What KIND of record this is lives
  // in the glyph's tooltip, never in a second line or a label beside the name.
  const renderHeading: ((headingTitle: string) => React.ReactNode) | undefined = renderTitle
    ? (headingTitle: string): React.ReactNode => (
        <span className="kuika-editor-header__heading">{renderTitle(headingTitle)}</span>
      )
    : undefined;

  return (
    <KuikaPageHeader
      variant="bar"
      className={className}
      title={title}
      icon={icon}
      renderTitle={renderHeading}
      center={center}
      onBack={onCancel}
      backLabel={backLabel}
      backTestId={cancelTestId}
      backOnboardingId={cancelOnboardingId}
      actions={
        <>
          {/* Editor-level controls (a mode switch), then whatever the caller leads with,
              then the commit button — the right edge every other editor's is on. */}
          {extra}
          {leading}
          {renderSubmit ? renderSubmit(submitButton) : submitButton}
        </>
      }
    />
  );
};

export default KuikaEditorHeader;
